import React from 'react';
import { CheckCircle, XCircle, Star } from 'lucide-react';
import { flagFor } from './jobFormat.js';

// Payment-status icon plus the hover card with client spend / hires / reviews.
// `isLastThree` flips the card to open upward so rows near the bottom of the
// page don't clip it. `job` + `client` (parsed client_data) drive the stats.
export const ClientTooltip = ({ job, client, isPaymentVerified, isLastThree }) => (
    <div className="relative group/tooltip">
        <div className="cursor-help">
            {isPaymentVerified ? (
                <CheckCircle className="w-4 h-4 text-green-500" fill="transparent" />
            ) : (
                <XCircle className="w-4 h-4 text-red-500" />
            )}
        </div>

        {/* Tooltip Content */}
        <div
            onClick={(e) => e.stopPropagation()}
            className={`absolute right-full mr-3 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-5 hidden group-hover/tooltip:block z-50 animate-in fade-in duration-200 text-left before:absolute before:left-full before:h-full before:w-3 before:content-[''] ${isLastThree ? 'bottom-[-10px] before:bottom-4' : 'top-[-20px] before:top-4'}`}
        >
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                    {isPaymentVerified ? (
                        <><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" /> Payment method verified</>
                    ) : (
                        <><XCircle className="w-4 h-4 text-gray-400 flex-shrink-0" /> Payment method unverified</>
                    )}
                </div>

                {/* Stats Block 1: Spend & Hires */}
                <div className="space-y-1">
                    <div className="text-gray-900 font-semibold text-base">
                        {(() => {
                            const spent = client.totalSpent?.rawValue || client.totalSpent;
                            return spent && !isNaN(parseFloat(spent))
                                ? `$${parseInt(spent).toLocaleString()} total spent`
                                : '$0 total spent';
                        })()}
                    </div>
                    <div className="text-xs text-gray-500">
                        {client.totalHires || 0} hires
                        {client.jobHires != null ? `, ${client.jobHires} for this job` : ''}
                    </div>
                </div>

                {/* Stats Block 2: Jobs */}
                <div className="space-y-1">
                    <div className="text-gray-900 text-sm">
                        <span className="font-medium">{client.totalPostedJobs || 0} jobs posted</span>
                    </div>
                    <div className="text-xs text-gray-500">
                        {job.total_applicants != null
                            ? `${job.total_applicants} applicant${job.total_applicants === 1 ? '' : 's'} so far`
                            : '— applicants'}
                    </div>
                </div>

                {/* Reviews */}
                <div className="space-y-1 pt-1">
                    <div className="flex items-center gap-1">
                        <div className="flex text-yellow-500">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={14} className={i < Math.round(client.totalFeedback || 0) ? "fill-current" : "text-gray-300"} />
                            ))}
                        </div>
                        <span className="text-xs text-gray-600 font-medium ml-1">
                            {client.totalFeedback?.toFixed?.(2) || '0.00'} of {client.totalReviews || 0} reviews
                        </span>
                    </div>
                </div>

                {/* Footer Meta */}
                <div className="space-y-1 pt-2 border-t border-gray-100 mt-2">
                    <div className="text-xs text-gray-600">
                        Registered: {client.memberSince ? new Date(client.memberSince).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-700 font-medium mt-1">
                        <span className="text-lg leading-none">{flagFor(client.country || job.country)}</span>
                        {job.country || client.country || 'Unknown Location'}
                    </div>
                    <div className="text-xs text-gray-500">
                        {client.timezone || client.location?.timezone || '-'}
                    </div>
                </div>
            </div>
        </div>
    </div>
);
